import React, { useCallback, useMemo } from "react";

import { Stack } from "@mui/material";
import { useParams } from "react-router-dom";
import { useNavigate } from "react-router-dom-v5-compat";

import { TaskStatus, TaskV3_1 } from "@fartherfinance/frontend/api/Tasks/Types";

import { useFilteredTransitionTasks } from "../hooks/useFilteredTransitionTasks";
import { useRetry } from "../hooks/useRetry";
import TransitionErrorState from "../ui/TransitionErrorState";
import TransitionHeader from "../ui/TransitionHeader";
import Text from "@src/sharedComponents/Typography/Text";

import OnboardingTaskCard from "./OnboardingTasks/OnboardingTaskCard";
import OnboardingTaskSkeleton from "./OnboardingTasks/OnboardingTaskSkeleton";

const MAX_NEXT_TASKS_TO_DISPLAY = 3;

const NextTasks: React.FC = () => {
  const navigate = useNavigate();
  const { transitionId } = useParams<{
    transitionId: string;
  }>();
  const { handleRetry } = useRetry("tasks");
  const { sortedTasks, isLoading, hasError } =
    useFilteredTransitionTasks("onboarding-tasks");

  const nextTasks = useMemo(() => {
    // filter out completed tasks, take first MAX_NEXT_TASKS_TO_DISPLAY
    return sortedTasks
      .filter((task) => task.taskStatus === TaskStatus.Enum.NOT_STARTED)
      .slice(0, MAX_NEXT_TASKS_TO_DISPLAY);
  }, [sortedTasks]);

  const handleOpenTaskDrawer = useCallback(
    (task: TaskV3_1) => {
      navigate(`/Advisor/Transitions/${transitionId}/Summary/${task.id}`);
    },
    [navigate, transitionId]
  );

  if (hasError) {
    return (
      <div>
        <TransitionHeader title="Next Onboarding Tasks" />
        <TransitionErrorState
          message="Could not load tasks"
          onRetry={handleRetry}
        />
      </div>
    );
  }

  return (
    <div>
      <TransitionHeader title="Next Onboarding Tasks" />
      <Stack spacing={2}>
        {isLoading ? (
          Array.from({ length: MAX_NEXT_TASKS_TO_DISPLAY }, (_, index) => (
            <OnboardingTaskSkeleton key={`skeleton-${index}`} />
          ))
        ) : nextTasks.length > 0 ? (
          nextTasks.map((task) => (
            <OnboardingTaskCard
              key={task.id}
              task={task}
              onClick={handleOpenTaskDrawer}
            />
          ))
        ) : (
          <Text variant="body" color="textSubtle">
            All onboarding tasks completed
          </Text>
        )}
      </Stack>
    </div>
  );
};

export default NextTasks;
